import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./TourListingForm.css";
import { getAgentDetails } from "../services/agentService";
import { toast } from 'react-toastify';
import LocationPicker from "../components/LocationPicker";

function TourListingForm() {

  const { guideUserId } = useParams();
  const navigate = useNavigate();

  const loggedUser = JSON.parse(localStorage.getItem("user"));
  const loggedUserId = loggedUser?.id;

  const [guideId, setGuideId] = useState(null);
  const [agentDetailsId, setAgentDetailsId] = useState(null);
  const [isAgent, setIsAgent] = useState(false);
  const [isGuideApproved, setIsGuideApproved] = useState(true);
  const [guideCheckLoading, setGuideCheckLoading] = useState(true);

  const [tourTypes, setTourTypes] = useState([]);
  const [groupPricings, setGroupPricings] = useState([]);
  const [tourInclusions, setTourInclusions] = useState([]);

  const [realImageFiles, setRealImageFiles] = useState([]);


  const [kokoSelected, setKokoSelected] = useState(false);

  const [newInclusion, setNewInclusion] = useState("");
  const [inclusionError, setInclusionError] = useState("");
  const BACKEND_URL = process.env.REACT_APP_API_URL;


  useEffect(() => {
    const userIdToUse = guideUserId ?? loggedUserId;

    if (!userIdToUse) return;

    axios
      .get(`${BACKEND_URL}/guide-listing/guide/${userIdToUse}`)
      .then((res) => setGuideId(res.data.guide_id))
      .catch((err) => console.error("Guide ID load error:", err));

    // Check if logged user is an agent
    if (guideUserId) {
      getAgentDetails().then((agent) => {
        if (agent?.status_id === 4) {
          setIsAgent(true);
          setAgentDetailsId(agent.id);
        }
      });
    }

    // Verify guide approval status
    async function verifyGuideApproval() {
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await axios.get(`${BACKEND_URL}/users/${userIdToUse}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const targetUser = res.data;
        const approved =
          targetUser?.status_id === 4 ||
          targetUser?.status_id === 5 ||
          targetUser?.Status?.name === "Approved" ||
          targetUser?.Status?.name === "Active" ||
          targetUser?.guide?.status_id === 4 ||
          targetUser?.guide?.status_id === 5;
        setIsGuideApproved(approved);
      } catch (err) {
        const approved = loggedUser?.status_id === 4 || loggedUser?.status_id === 5;
        setIsGuideApproved(approved);
      } finally {
        setGuideCheckLoading(false);
      }
    }
    verifyGuideApproval();
  }, [guideUserId, loggedUserId, BACKEND_URL, loggedUser?.status_id]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesRes, pricingRes, inclusionsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/guide-listing/`),
          axios.get(`${BACKEND_URL}/guide-listing/group-pricing`),
          axios.get(`${BACKEND_URL}/guide-listing/tour-inclusions`)
        ]);
        setTourTypes(typesRes.data);
        setGroupPricings(pricingRes.data);
        setTourInclusions(inclusionsRes.data);
      } catch (error) {
        console.error("Error fetching tour form options:", error);
      }
    };
    fetchData();
  }, [BACKEND_URL]);

  const handleAddInclusion = async () => {
    if (!newInclusion.trim()) {
      setInclusionError("Inclusion name is required");
      return;
    }

    try {
      const res = await axios.post(
        `${BACKEND_URL}/guide-listing/tour-inclusions`,
        { name: newInclusion.trim() }
      );

      const created = res.data.data;

      // Update inclusion list
      setTourInclusions((prev) => [...prev, created]);

      // Auto-select newly added inclusion
      setForm((prev) => ({
        ...prev,
        tour_inclusion_ids: [...prev.tour_inclusion_ids, created.id],
      }));

      setNewInclusion("");
      setInclusionError("");
      toast.success("Inclusion added!");
    } catch (err) {
      setInclusionError(
        err.response?.data?.error || "Failed to add inclusion"
      );
      toast.error("Failed to add inclusion");
    }
  };


  const [form, setForm] = useState({
    title: "",
    description: "",
    tour_type_id: "",
    currency_id: "",
    service_area: "",
    address: "",
    coordinates: "",
    tour_duration: "",
    start_time: "",
    price_per_hour: "",
    discount: "",
    coin_reward: "",
    max_people: "",
    group_pricing_ids: [],
    tour_inclusion_ids: [],
    image_urls: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };


  // Handle real image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setRealImageFiles(files);

    // Preview URLs
    const previews = files.map((f) => URL.createObjectURL(f));
    setForm((p) => ({ ...p, image_urls: previews }));
  };

  const uploadImages = async () => {
    let uploaded = [];

    for (let file of realImageFiles) {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${BACKEND_URL}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      uploaded.push(res.data.path);
    }

    return uploaded;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAgent && !isGuideApproved) {
      return toast.error("Your Guide account is pending approval by an administrator. You cannot create tour listings until approved.");
    }

    if (!guideId) {
      return toast.error("Guide ID not loaded. Please wait or refresh.");
    }

    // Build payment method array
    let paymentMethodIds = [];
    if (kokoSelected) paymentMethodIds.push(4);          // KoKo

    try {
      // Upload images
      const uploadedUrls = await uploadImages();

      const payload = {
        ...form,
        image_urls: uploadedUrls,
        guide_id: guideId,
        traveler_agent_details_id: isAgent ? agentDetailsId : null,
        tour_type_id: Number(form.tour_type_id),
        currency_id: 4,
        price_per_hour: Number(form.price_per_hour),
        discount: form.discount ? Number(form.discount) : 0,
        coin_reward: form.coin_reward ? Number(form.coin_reward) : 0,
        max_people: Number(form.max_people),
        tour_duration: Number(form.tour_duration),
        group_pricing_ids: form.group_pricing_ids.map(Number),
        tour_inclusion_ids: form.tour_inclusion_ids.map(Number),
        payment_method_ids: paymentMethodIds,
      };

      await axios.post(`${BACKEND_URL}/guide-listing/create`, payload);
      toast.success("Tour Listing Created Successfully!");
      navigate("/travelerhome");

    } catch (error) {
      console.error("Create listing error:", error.response?.data);
      toast.error(error.response?.data?.error || "Failed to create listing");
    }
  };

  const toggleInclusion = (id) => {
    setForm((prev) => {
      const active = prev.tour_inclusion_ids.includes(id);
      return {
        ...prev,
        tour_inclusion_ids: active
          ? prev.tour_inclusion_ids.filter((x) => x !== id)
          : [...prev.tour_inclusion_ids, id],
      };
    });
  };

  const toggleGroupPricing = (id) => {
    setForm((prev) => {
      const active = prev.group_pricing_ids.includes(id);
      return {
        ...prev,
        group_pricing_ids: active
          ? prev.group_pricing_ids.filter((x) => x !== id)
          : [...prev.group_pricing_ids, id],
      };
    });
  };

  if (!guideCheckLoading && !isGuideApproved && !isAgent) {
    return (
      <div className="tour-container">
        <div className="tour-info" style={{ textAlign: "center", padding: "50px 20px" }}>
          <div style={{ fontSize: "56px", marginBottom: "15px" }}>⏳</div>
          <h2 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "12px", color: "#1f2937" }}>
            Guide Account Pending Approval
          </h2>
          <p style={{ color: "#6b7280", lineHeight: "1.6", fontSize: "15px", marginBottom: "25px", maxWidth: "550px", margin: "0 auto 25px auto" }}>
            Your Tour Guide registration is currently under review by our administration team. You will be able to publish tour listings once your account has been approved.
          </p>
          <button 
            type="button"
            className="submit-btn" 
            style={{ maxWidth: "200px", margin: "0 auto", padding: "12px 24px", cursor: "pointer" }} 
            onClick={() => navigate(loggedUserId ? `/profile/${loggedUserId}` : "/travelerhome")}
          >
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tour-container">

      <div className="tour-info">
        <h2>Create Tour Listing</h2>

        <form className="tour-form" onSubmit={handleSubmit}>

          {/* Basic Fields */}
          <div className="form-group">
            <label>Tour Title</label>
            <input name="title" value={form.title} onChange={handleInputChange} required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tour Type</label>
              <select name="tour_type_id" value={form.tour_type_id} onChange={handleInputChange} required>
                <option value="">Select Type</option>
                {tourTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleInputChange} required />
          </div>

          <div className="form-group">
            <label>Service Area / Region</label>
            <input name="service_area" value={form.service_area} onChange={handleInputChange} placeholder="e.g. Kandy & Central Highlands" required />
          </div>

          <div className="form-group">
            <label>Meeting Point / Tour Address</label>
            <input name="address" value={form.address} onChange={handleInputChange} placeholder="e.g. Temple of the Tooth Entrance, Dalada Veediya, Kandy" />
          </div>

          <LocationPicker
            value={form.coordinates}
            onChange={(newCoords) => setForm((prev) => ({ ...prev, coordinates: newCoords }))}
            address={form.address}
            onAddressChange={(newAddr) => setForm((prev) => ({ ...prev, address: newAddr }))}
            label="Google Maps Meeting Point or Coordinates"
            placeholder="e.g. 7.2936, 80.6413 or https://maps.app.goo.gl/..."
            helpText="Pick the tour starting / meeting location on the interactive map or enter coordinates / Google Maps link manually."
          />

          <div className="form-row">
            <div className="form-group">
              <label>Price per Hour</label>
              <input name="price_per_hour" type="number" value={form.price_per_hour} onChange={handleInputChange} required />
            </div>

            <div className="form-group">
              <label>Max People</label>
              <input name="max_people" type="number" value={form.max_people} onChange={handleInputChange} required />
            </div>
          </div>

          {/* Offers */}
          <div className="form-group">
            <label>Discount (%)</label>
            <input name="discount" type="number" value={form.discount} onChange={handleInputChange} placeholder="e.g. 10" />
          </div>

          <div className="form-group">
            <label>Coin Reward</label>
            <input name="coin_reward" type="number" value={form.coin_reward} onChange={handleInputChange} placeholder="e.g. 50" />
          </div>

          {/* Time */}
          <div className="form-group">
            <label>Start Time</label>
            <input type="time" name="start_time" value={form.start_time} onChange={handleInputChange} required />
          </div>

          <div className="form-group">
            <label>Tour Duration (hours)</label>
            <input name="tour_duration" type="number" value={form.tour_duration} onChange={handleInputChange} required />
          </div>

          {/* Multi-select */}
          <div className="form-row">
            <div className="form-group">
              <label>Group Pricing</label>

              <div className="tl-pill-container">
                {groupPricings.map((gp) => (
                  <div
                    key={gp.id}
                    className={`tl-pill ${form.group_pricing_ids.includes(gp.id) ? "selected" : ""
                      }`}
                    onClick={() => toggleGroupPricing(gp.id)}
                  >
                    {gp.name}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>What's Included</label>

              <div className="tl-pill-container">
                {tourInclusions.map((inc) => (
                  <div
                    key={inc.id}
                    className={`tl-pill ${form.tour_inclusion_ids.includes(inc.id) ? "selected" : ""
                      }`}
                    onClick={() => toggleInclusion(inc.id)}
                  >
                    {inc.name}
                  </div>
                ))}
              </div>

              {/* Add New Inclusion */}
              <div className="add-inclusion-box">
                <input
                  type="text"
                  placeholder="Add new inclusion (e.g. Free Snacks)"
                  value={newInclusion}
                  onChange={(e) => setNewInclusion(e.target.value)}
                />
                <button type="button" onClick={handleAddInclusion}>
                  Add
                </button>
              </div>

              {inclusionError && (
                <p className="error-text">{inclusionError}</p>
              )}
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label>Media Uploads</label>
            <input type="file" multiple onChange={handleImageUpload} />
            <div className="image-preview">
              {form.image_urls.map((u, i) => (
                <img key={i} src={u} alt="" />
              ))}
            </div>
          </div>

          {/* Payment Methods */}
          <h3>Payment Methods</h3>
          <div className="payment-options">

            <label>
              <input
                type="checkbox"
                checked={kokoSelected}
                onChange={(e) => setKokoSelected(e.target.checked)}
              />
              KoKo Pay
            </label>
          </div>
          <button className="booking-button" type="submit">
            Create Tour Listing
          </button>
        </form>
      </div>

    </div>
  );
}

export default TourListingForm;
